import type { SVGProps } from 'react'

/**
 * The product mark (Paper bento): a lidded box seen from above with three compartments, the
 * signature, the text lines and the check. Below 28 px it draws the small cut, which drops the
 * text lines and sits on a 16 px pixel grid.
 *
 * The frame follows `--op-color-heading` (navy in light, white in dark), so the mark keeps its
 * edge on both themes. In dark the box's paper, its dividers and the signature take dark-surface
 * variants (`.op-brand-mark` in styles.css): a white frame around white paper read as one filled
 * white tile (iPhone app sweep, 19 Oct 2026). The compartment colours stay the brand's.
 */
export interface BrandMarkProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  readonly size?: number
  readonly label?: string
}

const NAVY = 'var(--op-brand-mark-divider, #0E2547)'
const PALE = '#E6F0FF'
const BLUE = '#0A60CF'
const INK = 'var(--op-brand-mark-ink, #0A60CF)'
const LINES = '#7B93B5'
const PAPER = 'var(--op-brand-mark-paper, #FFFFFF)'
/** The check on the blue compartment: white in both themes. */
const CHECK = '#FFFFFF'

export function BrandMark({ size = 36, label, className, ...rest }: BrandMarkProps) {
  const small = size < 28
  return (
    <svg
      viewBox={small ? '0 0 32 32' : '0 0 48 48'}
      width={size}
      height={size}
      fill="none"
      className={['op-brand-mark', className].filter(Boolean).join(' ')}
      {...(label === undefined
        ? { 'aria-hidden': true as const, focusable: 'false' as const }
        : { role: 'img' as const, 'aria-label': label })}
      {...rest}
    >
      {small ? (
        <>
          <rect x="2" y="2" width="28" height="28" rx="7" fill={PAPER} />
          <path d="M19 2h4a7 7 0 0 1 7 7v6H19Z" fill={PALE} />
          <path d="M19 15h11v8a7 7 0 0 1-7 7h-4Z" fill={BLUE} />
          <path d="M19 3v26M19 15h10" stroke={NAVY} strokeWidth="2" />
          <rect
            x="2"
            y="2"
            width="28"
            height="28"
            rx="7"
            stroke="var(--op-color-heading)"
            strokeWidth="4"
          />
          <path
            d="m21.6 22.6 1.9 1.9 3.3-3.5"
            stroke={CHECK}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.5 20.5c2-4.8 3.6-5.4 3.8-2.8.2 2-.4 3.4.8 3.1 1-.3 1.7-1.8 2.8-2.1.9-.3 1 1.6 2.1 1.4"
            stroke={INK}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <rect x="3" y="3" width="42" height="42" rx="11" fill={PAPER} />
          <path d="M26.5 3H34a11 11 0 0 1 11 11v9.5H26.5Z" fill={PALE} />
          <path d="M26.5 23.5H45V34a11 11 0 0 1-11 11h-7.5Z" fill={BLUE} />
          <path d="M26.5 4.5v39M26.5 23.5h17" stroke={NAVY} strokeWidth="3" />
          <rect
            x="3"
            y="3"
            width="42"
            height="42"
            rx="11"
            stroke="var(--op-color-heading)"
            strokeWidth="3"
          />
          <path
            d="m31.2 34.2 2.8 2.8 5.6-5.8"
            stroke={CHECK}
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.5 30c2.8-6.6 5-7.6 5.3-3.9.2 2.4-.6 4.8 1 4.4 1.3-.4 2.2-2.3 3.6-2.7 1.2-.4 1.3 2.5 3.4 2"
            stroke={INK}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M31 11.5h8M31 15.5h5.5" stroke={LINES} strokeWidth="2" strokeLinecap="round" />
        </>
      )}
    </svg>
  )
}
